import { DEFAULT_LANGUAGE, LANGUAGES, SupportedLanguage, useLanguage } from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import clientStorage from "@next-platform/client-common-core/services/clientStorage";
import {
  ButtonIcon,
  ButtonSize,
  ButtonVariant,
  Chip,
  ChipSize,
  DropDownMenu,
  IconVariants,
  Text,
  TextVariants,
} from "@next-platform/client-common-spicy-ui";
import React, { useCallback, useMemo, useState } from "react";

import { USER_LANGUAGE_PREFERENCE_LOCAL_STORAGE_KEY } from "@/constants";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import { cn } from "@/utils";

const localeSelectorLogger = appLogger.withTag("LocaleSelector");

const LocaleSelector = () => {
  const [stateIsDropdownOpen, setStateIsDropdownOpen] = useState(false);
  const { currentLanguage, availableLanguages } = useLanguage();
  const { pagePath, search } = useLocalizedLocation();

  // Stays synchronous: the navigation below must not wait on the write.
  const handleLanguageChange = useCallback((languageCode: SupportedLanguage) => {
    clientStorage.setItem(USER_LANGUAGE_PREFERENCE_LOCAL_STORAGE_KEY, languageCode as string).catch((writeError: unknown) => {
      localeSelectorLogger.error("Failed to persist the selected language", writeError);
    });
  }, []);

  const languageOptions = useMemo(() => {
    return Object.values(availableLanguages).map((language) => {
      const langPrefix = language.code === DEFAULT_LANGUAGE ? "" : `/${language.code}`;

      const baseUrl = pagePath === "/" ? langPrefix || "/" : `${langPrefix}${pagePath}`;

      const navigateTo = `${baseUrl}${search || ""}`;
      return {
        label: language.name,
        onPress: () => handleLanguageChange(language.code),
        classNames: {
          base: cn(currentLanguage === language.code && "bg-gray-8 dark:bg-gray-7"),
        },
        to: navigateTo,
        replace: true,
      };
    });
  }, [availableLanguages, currentLanguage, handleLanguageChange, search, pagePath]);

  return (
    <DropDownMenu listItems={languageOptions} isOpen={stateIsDropdownOpen} onOpenChange={setStateIsDropdownOpen}>
      <div
        className={cn(
          "hover:bg-gray-6 dark:hover:bg-gray-8 rounded-sm relative",
          stateIsDropdownOpen && "bg-gray-6 dark:bg-gray-8 border border-solid border-gray-3 dark:border-gray-4",
        )}
        data-testid="LocaleSelector"
      >
        <Chip className="absolute top-0 right-[-15%]" size={ChipSize.XSMALL}>
          <Text as="span" variant={TextVariants.LABEL_XSMALL_MEDIUM}>
            {LANGUAGES[currentLanguage].symbol}
          </Text>
        </Chip>
        <ButtonIcon icon={IconVariants.GLOBE} variant={ButtonVariant.TRANSPARENT} size={ButtonSize.MEDIUM} />
      </div>
    </DropDownMenu>
  );
};

export default React.memo(LocaleSelector);
